import React from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import GearIcon from "../../svg/GearIcon";
import PlusIcon from "../../svg/PlusIcon";
import GettingStartedCard from "../common/GettingStartedCard";
import useTheme from "../../../../common/hooks/useTheme";

type EmptyScreenType = "service" | "logic" | "lab";

interface ContentData {
  title: string;
  cardTitle: string;
  cardText: string;
  primaryButtonLabel: string;
  gettingStartedCard1Title: string;
  gettingStartedCard1Text: string;
  gettingStartedCard2Title: string;
  gettingStartedCard2Text: string;
  gettingStartedCard3Title: string;
  gettingStartedCard3Text: string;
}

const EmptyScreen = ({
  setOpenModal,
  type = "service",
}: {
  setOpenModal: (open: boolean) => void;
  type?: EmptyScreenType;
}) => {
  const theme: Theme = useTheme();
  const contentData: Record<EmptyScreenType, ContentData> = {
    service: {
      title: "",
      cardTitle: "No services created",
      cardText:
        "Create your first service to get started. services are used as outcomes in the logic builder.",
      primaryButtonLabel: "New Service",
      gettingStartedCard1Title: "Create services",
      gettingStartedCard1Text:
        "First, create services with their configurable settings. These will be available as outcomes in your logic rules.",
      gettingStartedCard2Title: "Build Logic Rules",
      gettingStartedCard2Text:
        "Create rules with conditions and assign your services as outcomes. Multiple rules are combined with AND logic.",
      gettingStartedCard3Title: "Monitor & Manage",
      gettingStartedCard3Text:
        "View your configured services and rules. Edit, delete, or create new ones as your needs change.",
    },
    logic: {
      title: "",
      cardTitle: "Build Logic for Service Delivery",
      cardText:
        "Define rules and outcomes to automate which services are provided in each case.",
      primaryButtonLabel: "New Logic",
      gettingStartedCard1Title: "Create Rules",
      gettingStartedCard1Text:
        "Create logical rules with conditions. Multiple rules will be combined with AND logic.",
      gettingStartedCard2Title: "Define Service Outcomes",
      gettingStartedCard2Text:
        "Select which services should be provided as outcomes for each rule.",
      gettingStartedCard3Title: "Set Default Values",
      gettingStartedCard3Text: "Configure default values for service settings.",
    },
    lab: {
      title: "",
      cardTitle: "Order Lab tests for your clients",
      cardText:
        "Create custom lab panels with biomarkers and order tests for your clients.",
      primaryButtonLabel: "New Panel",
      gettingStartedCard1Title: "Create a Panel",
      gettingStartedCard1Text:
        "Build custom lab panels by selecting biomarkers. See costs update in real-time as you add tests.",
      gettingStartedCard2Title: "Order for Client",
      gettingStartedCard2Text:
        "Select a client and assign a panel. The lab order will be sent to the provider automatically.",
      gettingStartedCard3Title: "Track Results",
      gettingStartedCard3Text:
        "Monitor order status from sample collection to completed results in the Panel Overview.",
    },
  };

  const content = contentData[type];
  return (
    <>
      {content.title !== "" && (
        <div style={styles.titleContainer}>
          <div style={styles.title(theme)}>{content.title}</div>
          {/*{content.secondaryButtonLabel && (*/}
          {/*  <PrimaryButton*/}
          {/*    label={'Logic builder'}*/}
          {/*    icon={<GearIcon fill={theme.colors_ds.NEUTRAL_GENERAL_WHITE} />}*/}
          {/*    onClick={content.secondaryButtonAction || (() => {})}*/}
          {/*  />*/}
          {/*)}*/}
        </div>
      )}
      <div style={styles.cardContainer(theme)}>
        <div style={styles.cardTitle(theme)}>{content.cardTitle}</div>
        <div style={styles.cardText(theme)}>{content.cardText}</div>
        <PrimaryButton
          label={content.primaryButtonLabel}
          icon={<PlusIcon fill={theme.colors_ds.NEUTRAL_GENERAL_WHITE} />}
          onClick={() => setOpenModal(true)}
        />
      </div>

      <>
        <div style={styles.subTitle(theme)}>Getting Started</div>
        <div style={styles.cardWrapper}>
          <GettingStartedCard
            index={1}
            title={content.gettingStartedCard1Title}
            text={content.gettingStartedCard1Text}
            color={theme.colorTokens.P[10]}
          />
          <GettingStartedCard
            index={2}
            title={content.gettingStartedCard2Title}
            text={content.gettingStartedCard2Text}
            color={theme.colorTokens.W[10]}
          />
          <GettingStartedCard
            index={3}
            title={content.gettingStartedCard3Title}
            text={content.gettingStartedCard3Text}
            color={theme.colorTokens.S[10]}
          />
        </div>
      </>
    </>
  );
};

const styles = {
  titleContainer: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: "16px",
  },
  title: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  cardContainer: (theme: any) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "32px",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
  }),
  cardTitle: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginBottom: "8px",
  }),
  cardText: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    marginBottom: "24px",
  }),
  subTitle: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginTop: "24px",
    marginBottom: "16px",
  }),
  cardWrapper: {
    display: "flex",
    flexDirection: "row" as "row",
    gap: "16px",
    width: "100%",
  },
};

export default EmptyScreen;
